import { z } from "zod";

/**
 * Standard outcomes for an ops-facing task-step card.
 *
 * These describe what the operator needs to do, not the underlying workflow engine state:
 * a completed workflow step may still require verification, while a paused workflow step may
 * require review.
 */
export const TaskStepCardStatus = {
  COMPLETED: "completed",
  FAILED: "failed",
  REVIEW_NEEDED: "review_needed",
  VERIFICATION_REQUIRED: "verification_required",
} as const;

export const taskStepCardStatusSchema = z.nativeEnum(TaskStepCardStatus);
export type TaskStepCardStatus = (typeof TaskStepCardStatus)[keyof typeof TaskStepCardStatus];

/**
 * Neutral timeline states are intentionally separate from the four actionable/terminal statuses.
 * They preserve progress context without implying that an operator should act.
 */
export const TaskStepCardInactiveStatus = {
  NOT_NEEDED: "not_needed",
  NOT_RUN: "not_run",
  WAITING: "waiting",
} as const;

export const taskStepCardInactiveStatusSchema = z.nativeEnum(TaskStepCardInactiveStatus);
export type TaskStepCardInactiveStatus = (typeof TaskStepCardInactiveStatus)[keyof typeof TaskStepCardInactiveStatus];

export type TaskStepCardState = TaskStepCardStatus | TaskStepCardInactiveStatus;

/**
 * What a step is asking of ops, which is what the card's colour, label and footer controls are
 * keyed on. Deliberately coarser than {@link TaskStepCardStatus}: the status says what the
 * workflow did, the phase says how much of the work is still a human's.
 *
 * Three phases, split by whether automation was *supposed* to finish the step and whether it did:
 *
 * - `QA` — automation owns the step end to end and it worked. Ops spot-checks the result, so the
 *   card carries the Correct / Incorrect pair.
 * - `ACTION_NEEDED` — the step was never fully automated; a human does the work themselves and
 *   then continues the workflow.
 * - `FAILED` — automation owns the step and it didn't land: the coverage didn't sync, no receiving
 *   provider was chosen, the authorization wasn't added. Also a human continuation, but this one
 *   is a defect rather than the design.
 */
export const TaskStepPhase = {
  QA: "qa",
  ACTION_NEEDED: "action_needed",
  FAILED: "failed",
} as const;

export type TaskStepPhase = (typeof TaskStepPhase)[keyof typeof TaskStepPhase];

/** `FAILED` is never a choice a step makes about itself — only a sync that didn't land produces it. */
export type PinnableTaskStepPhase = Exclude<TaskStepPhase, typeof TaskStepPhase.FAILED>;

/**
 * The phase a status maps to when a step doesn't pin one.
 *
 * Both `COMPLETED` and `VERIFICATION_REQUIRED` land on `QA`: each means automation produced a
 * result and a human is being asked to rule on it, which is one situation, not two. Neither is
 * green-and-final — automation finishing a step is not the same as anyone having checked it.
 */
const DEFAULT_PHASE_BY_STATUS: Record<TaskStepCardStatus, TaskStepPhase> = {
  [TaskStepCardStatus.COMPLETED]: TaskStepPhase.QA,
  [TaskStepCardStatus.FAILED]: TaskStepPhase.FAILED,
  [TaskStepCardStatus.REVIEW_NEEDED]: TaskStepPhase.ACTION_NEEDED,
  [TaskStepCardStatus.VERIFICATION_REQUIRED]: TaskStepPhase.QA,
};

function isActionableStatus(state: TaskStepCardState): state is TaskStepCardStatus {
  return state in DEFAULT_PHASE_BY_STATUS;
}

/**
 * Resolves the phase a card presents in, honouring a step's pinned phase but never letting it
 * mask reality.
 *
 * A pin is a statement about what the step is *for* — "this one was never fully automated, so it
 * always needs a human" — so it only applies while the step is actually asking something of ops.
 * A failed sync outranks any pin (red means red), and a step that hasn't run has no phase at all:
 * pinning one would colour a card for work nobody can do yet.
 */
export function resolveTaskStepPhase(
  state: TaskStepCardState,
  pinnedPhase?: PinnableTaskStepPhase
): TaskStepPhase | null {
  if (!isActionableStatus(state)) return null;
  if (state === TaskStepCardStatus.FAILED) return TaskStepPhase.FAILED;

  return pinnedPhase ?? DEFAULT_PHASE_BY_STATUS[state];
}
